import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { fetchRows, createRow, updateRow } from '../lib/api'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'

function getVal(v) {
  if (v === null || v === undefined) return ''
  if (typeof v === 'object' && 'value' in v) return v.value
  return v
}

function fmtDate(d) {
  if (!d) return ''
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const parts = String(d).split('-')
  if (parts.length !== 3) return d
  return `${parts[2]}-${months[parseInt(parts[1])-1]}-${parts[0]}`
}

const HOURS = ['0100','0200','0300','0400','0500','0600','0700','0800','0900','1000','1100','1200','1300','1400','1500','1600','1700','1800','1900','2000','2100','2200','2300','2400']

function emptyHours() {
  return HOURS.map(t => ({
    time: t, lat: '', lon: '', cog: '', gyro: '', mag: '', sog: '', stw: '',
    log_miles: '', wind_speed: '', wind_dir: '', pressure: '', sea_state: '',
    air_temp: '', sea_temp: '', visibility: '', rpm: '', er_check: '', watch_keeper: '',
    tender_check: '', remarks: ''
  }))
}

const EMPTY_HEADER = {
  date: '', dep_time: '', arr_time: '', origin: '', destination: '',
  dep_fuel: '', arr_fuel: '', fuel_consumption: '', num_crew: '', num_guests: '', notes: ''
}

export default function RunningLog() {
  const { config } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [view, setView] = useState('table')
  const [editRow, setEditRow] = useState(null)
  const [header, setHeader] = useState({ ...EMPTY_HEADER })
  const [hours, setHours] = useState(emptyHours())

  const load = useCallback(async () => {
    setLoading(true)
    try { const data = await fetchRows('running_log'); setRows(data || []) }
    catch (e) { setError('Failed to load') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  function openNew() {
    const now = new Date()
    setHeader({ ...EMPTY_HEADER, date: now.toISOString().split('T')[0] })
    setHours(emptyHours())
    setEditRow(null)
    setView('form')
  }

  function openView(row) {
    setHeader({
      date: getVal(row['Date']), dep_time: getVal(row['Departure Time']),
      arr_time: getVal(row['Arrival Time']), origin: getVal(row['Voyage Origin']),
      destination: getVal(row['Voyage Destination']), dep_fuel: getVal(row['Departure Fuel']),
      arr_fuel: getVal(row['Arrival Fuel']), fuel_consumption: getVal(row['Fuel Consumption']),
      num_crew: getVal(row['Number of Crew']), num_guests: getVal(row['Number of Guests']),
      notes: getVal(row['Notes'])
    })
    try {
      const h = JSON.parse(getVal(row['Hours']) || '[]')
      const full = emptyHours()
      h.forEach(entry => { const idx = full.findIndex(r => r.time === entry.time); if (idx >= 0) full[idx] = { ...full[idx], ...entry } })
      setHours(full)
    } catch { setHours(emptyHours()) }
    setEditRow(row)
    setView('form')
  }

  async function handleSave() {
    setSaving(true); setError('')
    try {
      const payload = {
        'Date': header.date, 'Departure Time': header.dep_time, 'Arrival Time': header.arr_time,
        'Voyage Origin': header.origin, 'Voyage Destination': header.destination,
        'Departure Fuel': header.dep_fuel, 'Arrival Fuel': header.arr_fuel,
        'Fuel Consumption': header.fuel_consumption, 'Number of Crew': header.num_crew,
        'Number of Guests': header.num_guests, 'Hours': JSON.stringify(hours),
        'Notes': header.notes
      }
      if (editRow) await updateRow('running_log', editRow.id, payload)
      else await createRow('running_log', payload)
      setView('table'); load()
    } catch (e) { setError('Failed to save: ' + (e.message || '')) }
    finally { setSaving(false) }
  }

  async function handleDelete(row) {
    const label = 'log entry ' + (getVal(row['Date']) || '')
    if (!confirm('Delete the ' + label + '? You can undo it for 10 seconds.')) return
    try { await deleteWithUndo(supabase, { table: 'running_log', id: row.id, label, onUndone: () => load() }); load() }
    catch (e) { setError('Failed to delete') }
  }

  function updHour(idx, key, val) {
    setHours(h => { const arr = [...h]; arr[idx] = { ...arr[idx], [key]: val }; return arr })
  }

  function updHeader(key, val) { setHeader(h => ({ ...h, [key]: val })) }

  // ── TABLE VIEW ──
  if (view === 'table') return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <button style={btnPrimary} onClick={openNew}>+ New Entry</button>
      </div>
      {error && <div style={{ color: '#b4313c', padding: '8px 16px' }}>{error}</div>}
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>No entries yet. Click <strong>+ New Entry</strong> to add the first voyage.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Date','From','To','Dep Time','Arr Time','Dep Fuel','Arr Fuel','Fuel Used','Crew','Guests',''].map(h => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={td}>{fmtDate(getVal(row['Date']))}</td>
                  <td style={td}>{getVal(row['Voyage Origin'])}</td>
                  <td style={td}>{getVal(row['Voyage Destination'])}</td>
                  <td style={td}>{getVal(row['Departure Time'])}</td>
                  <td style={td}>{getVal(row['Arrival Time'])}</td>
                  <td style={td}>{getVal(row['Departure Fuel'])}</td>
                  <td style={td}>{getVal(row['Arrival Fuel'])}</td>
                  <td style={td}>{getVal(row['Fuel Consumption'])}</td>
                  <td style={td}>{getVal(row['Number of Crew'])}</td>
                  <td style={td}>{getVal(row['Number of Guests'])}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={btnSm} onClick={() => openView(row)}>View</button>
                      <button style={{ ...btnSm, color: '#b4313c' }} onClick={() => handleDelete(row)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )

  // ── FORM / TEMPLATE VIEW ──
  return (
    <>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <button style={btnSecondary} onClick={() => setView('table')}>← Back</button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {error && <span style={{ color: '#b4313c', fontSize: 12 }}>{error}</span>}
          <button style={btnSecondary} onClick={() => window.print()}>🖨 Print</button>
          <button style={btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Entry'}</button>
        </div>
      </div>

      <div className="print-area lb-form" style={{ padding: '16px 24px', fontSize: 12, overflowX: 'auto' }}>

        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 2 }}>{config?.vessel_name?.toUpperCase()}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>RUNNING LOG BOOK</div>
        </div>

        {/* Header */}
        <div style={{ ...sectionBox, marginBottom: 12 }}>
          <div className="lb-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
            {[['Date','date','date'],['Departure Time','dep_time','text'],['Arrival Time','arr_time','text'],['Voyage Origin','origin','text'],['Voyage Destination','destination','text'],['Notes','notes','text']].map(([label, key, type]) => (
              <div key={key}>
                <div style={fieldLabel}>{label}</div>
                <input style={fieldInput} type={type} value={header[key] || ''} onChange={e => updHeader(key, e.target.value)} />
              </div>
            ))}
          </div>
        </div>

        {/* Hourly table */}
        <div style={{ overflowX: 'auto', marginBottom: 12 }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 1400 }}>
            <thead>
              <tr style={{ background: 'var(--surface2)' }}>
                <th style={hdr} rowSpan={2}>Time</th>
                <th style={hdr} colSpan={2}>Position</th>
                <th style={hdr} colSpan={5}>Courses and Speed</th>
                <th style={hdr}>Log</th>
                <th style={hdr} colSpan={7}>Weather</th>
                <th style={hdr}>RPM</th>
                <th style={hdr}>ER Check</th>
                <th style={hdr}>Watch Keeper</th>
                <th style={hdr}>Tender</th>
                <th style={hdr}>Remarks</th>
              </tr>
              <tr style={{ background: 'var(--surface2)' }}>
                <th style={hdr}>Lat</th>
                <th style={hdr}>Lon</th>
                <th style={hdr}>COG</th>
                <th style={hdr}>Gyro</th>
                <th style={hdr}>Mag</th>
                <th style={hdr}>SOG</th>
                <th style={hdr}>STW</th>
                <th style={hdr}>NM</th>
                <th style={hdr}>Wind Spd</th>
                <th style={hdr}>Wind Dir</th>
                <th style={hdr}>Pressure</th>
                <th style={hdr}>Sea State</th>
                <th style={hdr}>Air °C</th>
                <th style={hdr}>Sea °C</th>
                <th style={hdr}>Vis</th>
                <th style={hdr}></th>
                <th style={hdr}></th>
                <th style={hdr}></th>
                <th style={hdr}></th>
                <th style={hdr}></th>
              </tr>
            </thead>
            <tbody>
              {hours.map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ ...cell, fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>{row.time}</td>
                  {[['lat',60],['lon',60],['cog',40],['gyro',40],['mag',40],['sog',40],['stw',40],['log_miles',40],['wind_speed',50],['wind_dir',50],['pressure',50],['sea_state',60],['air_temp',40],['sea_temp',40],['visibility',40],['rpm',50],['er_check',50],['watch_keeper',80],['tender_check',50],['remarks',120]].map(([key, w]) => (
                    <td key={key} style={cell}>
                      <input
                        style={{ ...rowInput, width: w }}
                        value={row[key] || ''}
                        onChange={e => updHour(i, key, e.target.value)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Quick Save */}
        <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', margin: '12px 0' }}>
          <button style={btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : '💾 Save Entry'}</button>
        </div>

        {/* Footer */}
        <div className="lb-grid" style={{ ...sectionBox, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
          {[['Departure Fuel','dep_fuel'],['Arrival Fuel','arr_fuel'],['Fuel Consumption','fuel_consumption'],['Number of Crew','num_crew'],['Number of Guests','num_guests']].map(([label, key]) => (
            <div key={key}>
              <div style={fieldLabel}>{label}</div>
              <input style={fieldInput} type={key.includes('fuel') ? 'text' : 'number'} placeholder={key.includes('fuel') ? 'e.g. 4800 gal or 18000 L' : ''} value={header[key] || ''} onChange={e => updHeader(key, e.target.value)} />
            </div>
          ))}
        </div>

      </div>
    </>
  )
}

const th = { padding: '8px 12px', textAlign: 'left', fontSize: 11, color: 'var(--muted)', fontWeight: 600 }
const td = { padding: '8px 12px', color: 'var(--text)', verticalAlign: 'middle' }
const btnSm = { padding: '3px 8px', fontSize: 11, borderRadius: 4, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer' }
const btnPrimary = { padding: '8px 18px', borderRadius: 6, border: 'none', background: 'var(--gold)', color: '#000', fontWeight: 600, cursor: 'pointer', fontSize: 13 }
const btnSecondary = { padding: '8px 18px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer', fontSize: 13 }
const sectionBox = { border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: 'var(--surface)' }
const hdr = { padding: '4px 6px', border: '1px solid var(--border)', fontSize: 10, fontWeight: 600, color: 'var(--muted)', textAlign: 'center', whiteSpace: 'nowrap' }
const cell = { padding: '2px 4px', border: '1px solid var(--border)', verticalAlign: 'middle' }
const rowInput = { padding: '2px 4px', border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 11, outline: 'none' }
const fieldLabel = { fontSize: 11, color: 'var(--muted)', marginBottom: 4 }
const fieldInput = { padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, width: '100%', boxSizing: 'border-box' }
